import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/stores/auth.store';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';
import { ROUTES } from '@/routes/routes';

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user, setAuth, accessToken, refreshToken } = useAuthStore();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const set =
    (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!form.currentPassword) errs.currentPassword = 'Current password is required';
    if (form.newPassword.length < 8) errs.newPassword = 'New password must be at least 8 characters';
    if (form.newPassword !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setSaving(true);
    try {
      await authService.changePassword(form.currentPassword, form.newPassword);
      if (user && accessToken && refreshToken) {
        setAuth({ ...user, mustChangePassword: false }, accessToken, refreshToken);
      }
      toast.success('Password changed successfully');
      navigate(ROUTES.ROOT, { replace: true });
    } catch {
      toast.error('Failed to change password. Check your current password and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <FontAwesomeIcon icon={faKey} className="h-5 w-5 text-primary" />
          </div>
          <div className="text-center">
            <h1 className="text-lg font-semibold text-foreground">Change Your Password</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              You must set a new password before continuing.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField label="Current Password" htmlFor="cp-current" required error={errors.currentPassword}>
            <Input
              id="cp-current"
              type="password"
              value={form.currentPassword}
              onChange={set('currentPassword')}
              disabled={saving}
              autoComplete="current-password"
            />
          </FormField>
          <FormField label="New Password" htmlFor="cp-new" required error={errors.newPassword}>
            <Input
              id="cp-new"
              type="password"
              value={form.newPassword}
              onChange={set('newPassword')}
              disabled={saving}
              autoComplete="new-password"
            />
          </FormField>
          <FormField label="Confirm New Password" htmlFor="cp-confirm" required error={errors.confirmPassword}>
            <Input
              id="cp-confirm"
              type="password"
              value={form.confirmPassword}
              onChange={set('confirmPassword')}
              disabled={saving}
              autoComplete="new-password"
            />
          </FormField>
          <Button type="submit" disabled={saving} className="mt-2 w-full">
            {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Set New Password'}
          </Button>
        </form>
      </div>
    </div>
  );
}
